import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Icon } from '../kzIcons';
import MapView from '../MapView';

export const STAGES = [
  { id: 'requested',   label: 'Requested',   ico: 'send' },
  { id: 'matching',    label: 'Matching',    ico: 'radar' },
  { id: 'assigned',    label: 'Assigned',    ico: 'user' },
  { id: 'on_way',      label: 'On the way',  ico: 'truck' },
  { id: 'arrived',     label: 'Arrived',     ico: 'pin' },
  { id: 'in_progress', label: 'In progress', ico: 'wrench' },
  { id: 'completed',   label: 'Completed',   ico: 'flag' },
  { id: 'paid',        label: 'Paid',        ico: 'file' },
  { id: 'closed',      label: 'Rated',       ico: 'star' },
];

const idx = (id) => Math.max(0, STAGES.findIndex((stage) => stage.id === id));

// Order matters: the first pattern that matches wins.
const STAGE_RULES = [
  ['closed', /closed|rated/],
  ['paid', /paid/],
  ['completed', /complet/],
  ['in_progress', /in_progress|started/],
  ['arrived', /arrived/],
  ['on_way', /on_way|on_the_way|en_route/],
  ['assigned', /assign|accept|approv|confirm|quote/],
  ['matching', /offer|match|search|dispatch|pending/],
];

const norm = (value) => String(value || '').toLowerCase().replace(/[\s-]+/g, '_');

const stageFrom = (value) => {
  const text = norm(value);
  if (!text) return null;
  const hit = STAGE_RULES.find(([, re]) => re.test(text));
  return hit ? hit[0] : 'requested';
};

// dispatch_status and status can drift apart (e.g. status flips to "Paid"
// while dispatch_status still says "completed"), so read both and keep
// whichever is furthest along.
export function stageOf(job, rated = false) {
  if (rated || job?.rating) return 'closed';
  const found = [job?.dispatch_status, job?.status].map(stageFrom).filter(Boolean);
  if (!found.length) return 'requested';
  return found.reduce((a, b) => (idx(b) > idx(a) ? b : a));
}

const isCancelled = (job) => /cancel/i.test(String(job?.status || job?.dispatch_status || ''));

// The backend gives up matching with a "no match" dispatch status.
const isNoMatch = (job) => /no_?match/i.test(String(job?.dispatch_status || ''));

const STATUS_LABEL = {
  requested: 'Requested',
  matching: 'Matching',
  assigned: 'Assigned',
  on_way: 'On the way',
  arrived: 'Arrived',
  in_progress: 'In progress',
  completed: 'Completed',
  paid: 'Paid',
  closed: 'Rated',
};

const identity = (value) => value;

/* ───────────── data helpers ───────────── */

const fmtTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-KE', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const fmtDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const TIME_KEYS = {
  requested: ['requested_at', 'created_at', 'created'],
  matching: ['matching_at', 'dispatched_at'],
  assigned: ['assigned_at', 'accepted_at'],
  on_way: ['on_way_at', 'en_route_at'],
  arrived: ['arrived_at'],
  in_progress: ['in_progress_at', 'started_at'],
  completed: ['completed_at'],
  paid: ['paid_at'],
  closed: ['rated_at', 'closed_at'],
};

const rawStageTime = (job, id) => {
  if (job?.stage_times?.[id]) return job.stage_times[id];
  const key = (TIME_KEYS[id] || []).find((k) => job?.[k]);
  return key ? job[key] : null;
};
const stageTime = (job, id) => fmtTime(rawStageTime(job, id));

const skillName = (s) => (typeof s === 'string' ? s : s?.name || s?.title || s?.label || '');

const cInfo = (job) => {
  const c = job?.craftsman && typeof job.craftsman === 'object' ? job.craftsman : {};
  const years = c.years_experience ?? c.experience_years ?? c.experience ?? null;
  const skills = (Array.isArray(c.skills) ? c.skills : Array.isArray(c.services) ? c.services : [])
    .map(skillName)
    .filter(Boolean);
  const assigned = c.id !== null && c.id !== undefined;
  return {
    name: assigned ? c.full_name || c.name || job?.craftsman_name || null : job?.craftsman_name || null,
    phone: c.phone || c.phone_number || job?.craftsman_phone || null,
    rating: Number(c.average_rating) || 0,
    reviews: c.review_count ?? c.reviews_count ?? c.total_reviews ?? job?.craftsman_review_count ?? null,
    img: c.profile_url || c.profile || c.profile_image || c.avatar || null,
    trade: c.primary_service || job?.service || job?.service_name || 'Craftsman',
    years: years === null || years === '' ? null : /^\d+(\.\d+)?$/.test(String(years)) ? `${years} years experience` : String(years),
    vehicle: c.vehicle_plate || c.vehicle || job?.vehicle_plate || null,
    skills,
    verified: (c.is_approved === true || c.is_verified === true || c.verified === true) && c.status !== 'pending',
  };
};

const avatarUrl = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Craftsman')}&background=000000&color=fff000&size=160&bold=true`;

const kes = (amount) => `KSh ${Number(amount || 0).toLocaleString('en-KE')}`;

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function amountOf(job) {
  const q = job?.quote_details && typeof job.quote_details === 'object' ? job.quote_details : {};
  const fee = positive(job?.platform_fee ?? job?.fee) || null;
  const quoted = [job?.amount_due, job?.final_amount, job?.quote_total, q.total, q.grand_total, q.total_amount]
    .map(positive)
    .find(Boolean) || 0;
  if (quoted && job?.amount_source !== 'budget') return { total: quoted, source: 'quote', fee };
  const budget = positive(job?.amount_due) || positive(job?.budget);
  return budget ? { total: budget, source: 'budget', fee: null } : { total: 0, source: null, fee: null };
}

// [headline, supporting line]
const NOTE = {
  requested: ['Request received', 'We are preparing your request for dispatch.'],
  matching: ['Finding your craftsman', 'We are offering the job to a suitable craftsman. If they can\'t come, we\'ll try the next match.'],
  assigned: ['Craftsman assigned', 'They accepted your request. Keep your phone nearby for updates.'],
  on_way: ['Your craftsman is on the way', 'They are travelling to your address.'],
  arrived: ['Your craftsman has arrived', 'Meet them so you can confirm the work before it starts.'],
  in_progress: ['Work in progress', 'You\'ll be notified when the work is marked complete.'],
  completed: ['Work completed', 'Check the photos, then pay with M-Pesa.'],
  paid: ['Payment received', 'Leave a rating to close the job.'],
  closed: ['Job closed', 'Thanks for using KaaKazini. This job is now in your history.'],
};

/* ───────────── map lookup (OpenStreetMap Nominatim) ───────────── */

function useGeocode(address, county) {
  const [geo, setGeo] = useState(null);
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    const addr = String(address || '').trim();
    const area = String(county || '').trim();
    if (!addr && !area) {
      setGeo(null);
      setStatus('idle');
      return undefined;
    }

    const ctrl = new AbortController();
    setStatus('loading');

    const lookup = async (q) => {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=ke&q=${encodeURIComponent(q)}`,
        { signal: ctrl.signal, headers: { Accept: 'application/json' } }
      );
      if (!r.ok) return null;
      const d = await r.json();
      return d[0] ? { lat: Number(d[0].lat), lon: Number(d[0].lon) } : null;
    };

    const t = setTimeout(async () => {
      try {
        const withArea = addr && area && !addr.toLowerCase().includes(area.toLowerCase()) ? `${addr}, ${area}` : addr || area;
        const exact = addr ? await lookup(`${withArea}, Kenya`) : null;
        if (exact) {
          setGeo({ ...exact, precise: true });
          setStatus('ok');
          return;
        }
        const wide = area ? await lookup(`${area} County, Kenya`) : null;
        setGeo(wide ? { ...wide, precise: false } : null);
        setStatus(wide ? 'ok' : 'fail');
      } catch (e) {
        if (e.name !== 'AbortError') {
          setGeo(null);
          setStatus('fail');
        }
      }
    }, 300);

    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [address, county]);

  return { geo, status };
}

/*
  Design notes
  - Brand stays black + signal yellow, but yellow is rationed: it only marks
    "where things are now" (progress, the live pin, the ETA) and the one money
    action (Pay). Everything else is black, white and warm grey.
  - The one bold moment is the black status panel. Quiet surfaces elsewhere.
  - Type: Bricolage Grotesque for headlines and amounts, Instrument Sans for
    everything else.
*/
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Instrument+Sans:wght@400;500;600;700&display=swap');

.kz {
  --ink: #000;
  --ink-2: #1d1d1b;
  --muted: #6d6d68;
  --fill: #f4f4f1;
  --fill-2: #e9e9e4;
  --line: #e7e7e2;
  --accent: #fff000;
  --accent-soft: #fffbc7;
  --good: #1f7a3d;
  --bad: #b3261e;
  --bg: #fff;
  --display: 'Bricolage Grotesque', 'Instrument Sans', system-ui, sans-serif;
  --r-lg: 24px;
  --r-md: 16px;
  --r-sm: 10px;
  width: 100%; max-width: 1040px; margin: 0 auto;
  color: var(--ink); font-family: 'Instrument Sans', system-ui, sans-serif; font-size: 15px; line-height: 1.5;
  -webkit-font-smoothing: antialiased;
}
.kz *, .kz *::before, .kz *::after { box-sizing: border-box; }
.kz button, .kz input, .kz textarea { font-family: inherit; }
.kz :focus-visible { outline: 3px solid var(--ink); outline-offset: 2px; border-radius: 6px; }
.kz-status-card :focus-visible, .kz-btn.pay:focus-visible { outline-color: var(--accent); }
.kz-btn.pay:focus-visible { outline-color: var(--ink); }
.kz svg { flex-shrink: 0; }
.kz-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* buttons */
.kz-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 50px; padding: 0 22px; border: 0; border-radius: 12px; background: var(--ink); color: #fff; font-size: 15px; font-weight: 600; text-decoration: none; cursor: pointer; white-space: nowrap; transition: background-color .15s, transform .1s; }
.kz-btn:hover:not(:disabled) { background: var(--ink-2); }
.kz-btn:active:not(:disabled) { transform: scale(.98); }
.kz-btn:disabled { background: #b9b9b3; color: #fff; cursor: default; }
.kz-btn.light { background: var(--fill); color: var(--ink); }
.kz-btn.light:hover:not(:disabled) { background: var(--fill-2); }
.kz-btn.light:disabled { background: var(--fill); color: #9a9a94; }
.kz-btn.sm { min-height: 38px; padding: 0 16px; font-size: 13px; border-radius: 999px; }
.kz-btn.block { width: 100%; }
.kz-btn.pay { min-height: 54px; background: var(--accent); color: var(--ink); font-size: 16px; font-weight: 700; box-shadow: inset 0 0 0 2px var(--ink); }
.kz-btn.pay:hover:not(:disabled) { background: #ffe600; }
.kz-btn.pay:disabled { background: var(--fill); color: #9a9a94; box-shadow: none; }
.kz-round { display: grid; place-items: center; width: 44px; height: 44px; flex: 0 0 44px; border: 0; border-radius: 50%; background: var(--fill); color: var(--ink); text-decoration: none; cursor: pointer; }
.kz-round:hover { background: var(--fill-2); }
.kz-round:disabled { opacity: .4; cursor: default; }
.kz-round[aria-disabled="true"] { opacity: .4; pointer-events: none; }
.kz-link { padding: 0; border: 0; background: none; color: var(--ink); font-size: 14px; font-weight: 600; text-decoration: underline; text-underline-offset: 3px; text-decoration-thickness: 1.5px; cursor: pointer; }
.kz-link:disabled { color: #9a9a94; text-decoration: none; cursor: default; }

/* top bar */
.kz-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; min-height: 40px; }
.kz-top-l, .kz-top-r { display: flex; align-items: center; gap: 8px; min-width: 0; }
.kz-back { display: grid; place-items: center; width: 42px; height: 42px; border: 0; border-radius: 50%; background: var(--fill); color: var(--ink); cursor: pointer; }
.kz-back:hover { background: var(--fill-2); }
.kz-ref { color: var(--muted); font-size: 13px; font-weight: 500; }
.kz-cancel { color: var(--bad); }

/* hero: map with controls floating on top, like a ride screen */
.kz-hero { position: relative; }
.kz-top.float { position: absolute; top: 14px; left: 14px; right: 14px; z-index: 3; margin: 0; min-height: 0; }
.kz-top.float .kz-back { background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.2); }
.kz-top.float .kz-back:hover { background: var(--fill); }
.kz-top.float .kz-ref { padding: 9px 14px; border-radius: 999px; background: #fff; color: var(--ink); font-weight: 600; box-shadow: 0 2px 10px rgba(0,0,0,.2); }
.kz-top.float .kz-btn.light { background: #fff; box-shadow: 0 2px 10px rgba(0,0,0,.2); }
.kz-top.float .kz-btn.light:hover:not(:disabled) { background: var(--fill); }
.kz-pin-pill { position: absolute; left: 14px; bottom: 16px; z-index: 3; display: flex; align-items: center; gap: 8px; max-width: calc(100% - 28px); padding: 9px 16px 9px 12px; border-radius: 999px; background: var(--ink); color: #fff; box-shadow: 0 4px 14px rgba(0,0,0,.25); font-size: 14px; font-weight: 600; }
.kz-pin-pill svg { color: var(--accent); }
.kz-pin-pill span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* map (colour tiles are drawn by MapView) */
.kz-map { position: relative; height: 320px; border-radius: var(--r-lg); overflow: hidden; background: #e9eef1; }
.kz-map-empty { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 0 24px; color: var(--muted); font-size: 14px; text-align: center; background:
  radial-gradient(circle at 1px 1px, #d9d9d3 1px, transparent 1.5px) 0 0 / 18px 18px, var(--fill); }
.kz-map-empty svg { padding: 10px; width: 44px; height: 44px; border-radius: 50%; background: var(--ink); color: var(--accent); }

/* sheet */
.kz-sheet { position: relative; }
.kz-grab { display: none; }
.kz-cols { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 40px; align-items: start; padding-top: 24px; }
.kz-col { min-width: 0; }
.kz-side { position: sticky; top: 16px; }
.kz-pay-row span:last-child, .kz-rcpt-row span:last-child { font-variant-numeric: tabular-nums; }

/* status panel: the one loud element */
.kz-status-card { padding: 22px 22px 20px; border-radius: var(--r-lg); background: var(--ink); color: #fff; }
.kz-status-card.off { background: var(--fill); color: var(--ink); }
.kz-head { display: flex; align-items: flex-start; gap: 14px; }
.kz-head-text { flex: 1; min-width: 0; }
.kz-status { display: grid; place-items: center; width: 50px; height: 50px; flex: 0 0 50px; border-radius: 16px; background: var(--accent); color: var(--ink); }
.kz-status-card.off .kz-status { background: var(--ink); color: var(--accent); }
.kz-h { margin: 0; font-family: var(--display); font-size: clamp(26px, 5.4vw, 36px); line-height: 1.08; font-weight: 700; letter-spacing: -.035em; text-wrap: balance; }
.kz-lead { margin: 8px 0 0; color: rgba(255,255,255,.7); font-size: 15px; line-height: 1.5; max-width: 50ch; }
.kz-status-card.off .kz-lead { color: var(--muted); }
.kz-eta { flex: 0 0 auto; margin-left: auto; font-family: var(--display); font-variant-numeric: tabular-nums; padding: 8px 14px; border-radius: 14px; background: var(--accent); color: var(--ink); font-size: 20px; font-weight: 700; line-height: 1.15; letter-spacing: -.02em; text-align: center; white-space: nowrap; }
.kz-eta small { display: block; margin-bottom: 1px; color: rgba(0,0,0,.65); font-family: 'Instrument Sans', system-ui, sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0; }
.kz-eta.done { background: #fff; }

/* progress */
.kz-prog { display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 4px; margin: 24px 0 12px; }
.kz-prog i { height: 6px; border-radius: 3px; background: rgba(255,255,255,.18); }
.kz-prog i.done { background: var(--accent); }
.kz-prog i.now { background: var(--accent); box-shadow: 0 0 0 3px rgba(255,240,0,.28); }
.kz-prog-meta { display: flex; justify-content: space-between; gap: 12px; color: rgba(255,255,255,.6); font-size: 13px; }
.kz-prog-meta b { color: #fff; font-weight: 600; }
.kz-load { position: relative; height: 6px; margin: 24px 0 12px; border-radius: 3px; background: rgba(255,255,255,.18); overflow: hidden; }
.kz-load::after { content: ''; position: absolute; top: 0; bottom: 0; width: 35%; background: var(--accent); border-radius: 3px; animation: kz-slide 1.4s ease-in-out infinite; }
@keyframes kz-slide { 0% { left: -35%; } 100% { left: 100%; } }
@media (prefers-reduced-motion: reduce) { .kz-load::after { animation: none; left: 0; width: 35%; } }

/* sections */
.kz-block { margin-top: 28px; padding-top: 26px; border-top: 1px solid var(--line); }
.kz-block:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
.kz-block-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 0 0 14px; }
.kz-block-title { margin: 0; font-family: var(--display); font-size: 20px; font-weight: 700; letter-spacing: -.025em; }
.kz-hint { margin: 10px 0 0; color: var(--muted); font-size: 13px; line-height: 1.55; }

/* side column cards */
.kz-side .kz-block { margin-top: 0; padding: 20px; border: 1px solid var(--line); border-radius: var(--r-md); }
.kz-side .kz-block + .kz-block { margin-top: 14px; }
.kz-side .kz-block.kz-paycard { border: 0; background: var(--fill); }

/* notices */
.kz-note { display: flex; align-items: flex-start; gap: 10px; margin-top: 20px; padding: 14px 16px; border-radius: 14px; background: var(--fill); font-size: 14px; line-height: 1.55; }
.kz-note svg { margin-top: 2px; }
.kz-note .kz-link { margin-left: 6px; }
.kz-quote { margin-top: 20px; padding: 18px; border: 2px solid var(--ink); border-radius: var(--r-md); background: var(--accent-soft); }
.kz-quote b { display: block; font-family: var(--display); font-size: 17px; font-weight: 700; letter-spacing: -.02em; }
.kz-quote p { margin: 2px 0 14px; color: #4a4a45; font-size: 14px; }
.kz-quote-btns { display: flex; gap: 8px; flex-wrap: wrap; }
.kz-quote .kz-btn.light { background: #fff; }

/* craftsman */
.kz-card { padding: 18px; border-radius: var(--r-md); background: var(--fill); }
.kz-who { display: flex; align-items: center; gap: 14px; }
.kz-rating { display: flex; align-items: center; gap: 6px; margin-top: 6px; font-size: 14px; font-weight: 600; }
.kz-rating svg { color: var(--ink); }
.kz-rating > svg { box-sizing: content-box; padding: 4px; border-radius: 50%; background: var(--accent); }
.kz-rating span { color: var(--muted); font-weight: 400; }
.kz-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 16px; }
.kz-btn.white { background: #fff; color: var(--ink); box-shadow: inset 0 0 0 1.5px var(--fill-2); }
.kz-btn.white:hover:not(:disabled) { background: #fafaf8; box-shadow: inset 0 0 0 1.5px var(--ink); }
.kz-btn.white:disabled { background: #fff; color: #9a9a94; box-shadow: inset 0 0 0 1.5px var(--fill-2); }
.sk { background: linear-gradient(90deg, #e7e7e2, #f6f6f3, #e7e7e2); background-size: 200% 100%; animation: kz-sh 1.4s linear infinite; }
.sk-line { display: block; height: 14px; margin: 6px 0; border-radius: 4px; }
@keyframes kz-sh { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) { .sk { animation: none; } }
.kz-av { width: 68px; height: 68px; flex: 0 0 68px; border: 3px solid var(--accent); border-radius: 50%; object-fit: cover; background: var(--fill); }
.kz-who-body { flex: 1; min-width: 0; }
.kz-who-name { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-family: var(--display); font-size: 20px; font-weight: 700; letter-spacing: -.02em; overflow-wrap: anywhere; }
.kz-tag { padding: 2px 8px; border-radius: 999px; background: var(--ink); color: var(--accent); font-family: 'Instrument Sans', system-ui, sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 0; }
.kz-who-meta { margin-top: 2px; color: var(--muted); font-size: 14px; }
.kz-kv { display: grid; grid-template-columns: 110px minmax(0, 1fr); gap: 16px; padding: 12px 0; border-bottom: 1px solid var(--line); font-size: 14px; }
.kz-kv:last-child { border-bottom: 0; }
.kz-kv > span:first-child { color: var(--muted); }
.kz-kv > span:last-child { overflow-wrap: anywhere; white-space: pre-wrap; font-weight: 500; }
.kz-kv-list { margin-top: 16px; border-top: 1px solid var(--line); }
.kz-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.kz-chip { padding: 4px 11px; border-radius: 999px; background: var(--fill); font-size: 13px; font-weight: 500; }
.kz-plate { padding: 3px 9px; border: 2px solid var(--ink); border-radius: 6px; background: var(--accent); font-size: 13px; font-weight: 700; letter-spacing: .05em; white-space: nowrap; }

/* updates list */
.kz-ups { margin: 0; padding: 0; list-style: none; }
.kz-up { position: relative; display: grid; grid-template-columns: 24px minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 10px 0; font-size: 14px; }
.kz-up::before { content: ''; position: absolute; left: 11px; top: -50%; bottom: 50%; width: 2px; background: var(--fill-2); }
.kz-up:first-child::before { display: none; }
.kz-up.done::before, .kz-up.now::before { background: var(--ink); }
.kz-up i { position: relative; justify-self: center; width: 10px; height: 10px; border-radius: 50%; background: var(--fill-2); }
.kz-up.done i { background: var(--ink); }
.kz-up.now i { width: 18px; height: 18px; background: var(--accent); border: 4px solid var(--ink); }
.kz-up.todo { color: #9a9a94; }
.kz-up.now { font-weight: 700; }
.kz-up time { color: var(--muted); font-size: 13px; font-weight: 400; font-variant-numeric: tabular-nums; }

/* details */
.kz-det { display: flex; gap: 14px; }
.kz-det-img, .kz-det-ph { width: 88px; height: 88px; flex: 0 0 88px; border-radius: 12px; object-fit: cover; background: var(--fill); }
.kz-det-ph { display: grid; place-items: center; color: #b0b0aa; }
.kz-det-body { min-width: 0; font-size: 14px; }
.kz-det-body b { display: block; font-family: var(--display); font-size: 17px; font-weight: 700; letter-spacing: -.02em; overflow-wrap: anywhere; }
.kz-det-body p { margin: 4px 0 0; color: #3a3a36; line-height: 1.55; overflow-wrap: anywhere; }
.kz-det-loc { display: flex; align-items: flex-start; gap: 6px; margin-top: 8px; color: var(--muted); font-size: 13px; }
.kz-det-loc svg { margin-top: 2px; }

/* photos */
.kz-photos { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.kz-photo { position: relative; aspect-ratio: 1; padding: 0; border: 0; border-radius: 12px; overflow: hidden; background: var(--fill); cursor: pointer; }
.kz-photo img { display: block; width: 100%; height: 100%; object-fit: cover; transition: transform .25s; }
.kz-photo:hover img { transform: scale(1.04); }
.kz-photo.more { display: grid; place-items: center; background: var(--ink); color: var(--accent); font-family: var(--display); font-size: 22px; font-weight: 700; }
.kz-photos-empty { padding: 16px; border: 1.5px dashed var(--fill-2); border-radius: 12px; color: var(--muted); font-size: 14px; line-height: 1.55; }

/* payment */
.kz-pay-row { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; font-size: 14px; color: #3a3a36; }
.kz-pay-row.total { align-items: baseline; margin-top: 6px; padding-top: 14px; border-top: 1px solid var(--fill-2); color: var(--ink); font-size: 15px; font-weight: 600; }
.kz-pay-row.total span:last-child { font-family: var(--display); font-size: 28px; font-weight: 700; letter-spacing: -.03em; }
.kz-mpesa { display: inline-grid; place-items: center; height: 24px; padding: 0 8px; border-radius: 6px; background: var(--good); color: #fff; font-size: 11px; font-weight: 700; letter-spacing: .02em; }
.kz-paid { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 700; }
.kz-paid .kz-round { width: 26px; height: 26px; flex-basis: 26px; background: var(--ink); color: var(--accent); }
.kz-pay-actions { display: grid; gap: 12px; margin-top: 16px; justify-items: start; }

/* receipt */
.kz-rcpt { margin-top: 14px; padding: 16px; border-radius: 12px; background: #fff; }
.kz-rcpt h4 { margin: 0 0 6px; font-family: var(--display); font-size: 15px; font-weight: 700; }
.kz-rcpt-row { display: flex; justify-content: space-between; gap: 12px; padding: 6px 0; font-size: 13px; }
.kz-rcpt-row span:first-child { color: var(--muted); }
.kz-rcpt-row span:last-child { font-weight: 600; text-align: right; overflow-wrap: anywhere; }
.kz-rcpt-row.total { margin-top: 4px; padding-top: 10px; border-top: 1px solid var(--fill-2); font-size: 15px; font-weight: 700; }
.kz-rcpt-row.total span { color: var(--ink) !important; }
.kz-rcpt p { margin: 8px 0 12px; color: var(--muted); font-size: 12px; }

/* rating */
.kz-stars { display: flex; gap: 8px; margin: 14px 0; }
.kz-stars button { width: 48px; height: 48px; padding: 0; border: 0; border-radius: 50%; background: var(--fill); color: #bdbdb6; font-size: 24px; line-height: 1; cursor: pointer; transition: background-color .15s, transform .1s; }
.kz-stars button:hover { background: var(--fill-2); }
.kz-stars button:active { transform: scale(.94); }
.kz-stars button.on { background: var(--ink); color: var(--accent); }
.kz-textarea { display: block; width: 100%; min-height: 104px; padding: 14px 16px; border: 2px solid transparent; border-radius: 14px; background: var(--fill); color: var(--ink); font-size: 16px; line-height: 1.55; resize: vertical; }
.kz-textarea:focus { outline: none; border-color: var(--ink); background: #fff; }
.kz-textarea::placeholder { color: #8e8e88; }
.kz-err { margin: 8px 0 0; color: var(--bad); font-size: 14px; font-weight: 500; }

/* empty */
.kz-empty { max-width: 420px; margin: 0 auto; padding: clamp(32px, 9vw, 72px) 0 40px; text-align: center; }
.kz-empty-ico { display: grid; place-items: center; width: 72px; height: 72px; margin: 0 auto 22px; border-radius: 22px; background: var(--ink); color: var(--accent); }
.kz-empty h3 { margin: 0 0 8px; font-family: var(--display); font-size: 30px; font-weight: 700; letter-spacing: -.035em; }
.kz-empty p { margin: 0 0 26px; color: var(--muted); line-height: 1.55; }
.kz-empty-btns { display: grid; gap: 8px; }

.kz-picker { display: flex; gap: 8px; overflow-x: auto; padding: 2px 1px 14px; }
.kz-picker .kz-btn { flex: 0 0 auto; }

@media (max-width: 900px) {
  .kz-side { position: static; }
  .kz-cols { grid-template-columns: minmax(0, 1fr); gap: 0; }
  .kz-side { margin-top: 28px; padding-top: 0; }
}
@media (max-width: 600px) {
  .kz-map { height: 36vh; min-height: 240px; border-radius: 0 0 var(--r-lg) var(--r-lg); }
  .kz-pin-pill { bottom: 36px; }
  .kz-actions .kz-btn { padding: 0 10px; }
  .kz-sheet.over { margin: -22px 0 0; padding: 10px 0 0; border-radius: 24px 24px 0 0; background: var(--bg); box-shadow: 0 -10px 24px rgba(0,0,0,.1); position: relative; z-index: 2; }
  .kz-sheet.over .kz-grab { display: block; width: 40px; height: 5px; margin: 0 auto; border-radius: 3px; background: #d4d4ce; }
  .kz-cols { padding-top: 18px; }
  .kz-status-card { padding: 18px 16px 16px; }
  .kz-status { width: 44px; height: 44px; flex-basis: 44px; border-radius: 14px; }
  .kz-kv { grid-template-columns: 92px minmax(0, 1fr); gap: 12px; }
  .kz-head { flex-wrap: wrap; }
  .kz-eta { margin-left: 58px; }
}
@media print {
  body * { visibility: hidden !important; }
  .kz-rcpt, .kz-rcpt * { visibility: visible !important; }
  .kz-rcpt { position: absolute; inset: 0 auto auto 0; width: 100%; background: #fff; }
}
`;

/* ───────────── pieces ───────────── */

function JobMap({ geo, status, hasPlace, place }) {
  return (
    <div className="kz-map">
      {geo && place && (
        <div className="kz-pin-pill"><Icon name="pin" size={15} stroke={2.2} /><span>{place}</span></div>
      )}
      {geo ? (
        <MapView lat={geo.lat} lon={geo.lon} precise={geo.precise} />
      ) : (
        <div className="kz-map-empty">
          <Icon name="pin" size={22} />
          <span>
            {!hasPlace ? 'No address on this request.' : status === 'fail' ? 'Map unavailable right now.' : 'Finding the location'}
          </span>
        </div>
      )}
    </div>
  );
}

function Progress({ currentIndex, searching, stage }) {
  const total = STAGES.length;
  const step = Math.min(currentIndex + 1, total);
  return (
    <>
      {searching ? (
        <div className="kz-load" role="progressbar" aria-label="Searching for a craftsman" />
      ) : (
        <div
          className="kz-prog"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={total}
          aria-valuenow={step}
          aria-label={`Step ${step} of ${total}: ${STATUS_LABEL[stage]}`}
        >
          {STAGES.map((s, i) => (
            <i key={s.id} className={i < currentIndex ? 'done' : i === currentIndex ? 'now' : ''} />
          ))}
        </div>
      )}
      <div className="kz-prog-meta">
        <b>{STATUS_LABEL[stage]}</b>
        <span>Step {step} of {total}</span>
      </div>
    </>
  );
}

function Updates({ job, currentIndex }) {
  return (
    <ol className="kz-ups" aria-label="Job updates">
      {STAGES.map((s, i) => {
        const state = i < currentIndex ? 'done' : i === currentIndex ? 'now' : 'todo';
        const t = state === 'todo' ? null : stageTime(job, s.id);
        return (
          <li key={s.id} className={`kz-up ${state}`} aria-current={state === 'now' ? 'step' : undefined}>
            <i />
            <span>{s.label}</span>
            <time>{t || ''}</time>
          </li>
        );
      })}
    </ol>
  );
}

function CraftsmanSkeleton() {
  return (
    <section className="kz-block" aria-label="Your craftsman">
      <div className="kz-card">
        <div className="kz-who">
          <span className="kz-av sk" />
          <div className="kz-who-body">
            <span className="sk-line sk" style={{ width: '55%' }} />
            <span className="sk-line sk" style={{ width: '35%' }} />
          </div>
        </div>
        <p className="kz-hint">Their name, rating and phone number will show here once someone accepts.</p>
      </div>
    </section>
  );
}

function Craftsman({ craftsman, job, stage, mediaUrl, onMessage }) {
  const reached = ['arrived', 'in_progress', 'completed', 'paid', 'closed'].includes(stage);
  const where = job.craftsman_location || (reached ? job.address || job.location : null);
  const avatar = mediaUrl(craftsman.img) || avatarUrl(craftsman.name);
  const meta = [craftsman.trade, craftsman.years].filter(Boolean).join(', ');

  return (
    <section className="kz-block" aria-label="Your craftsman">
      <div className="kz-card">
        <div className="kz-who">
          <img
            className="kz-av"
            src={avatar}
            alt=""
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = avatarUrl(craftsman.name); }}
          />
          <div className="kz-who-body">
            <div className="kz-who-name">
              {craftsman.name}
              {craftsman.verified && <span className="kz-tag">Verified</span>}
            </div>
            {meta && <div className="kz-who-meta">{meta}</div>}
            <div className="kz-rating">
              {craftsman.rating > 0 ? (
                <>
                  <Icon name="star" size={11} stroke={0} fill="currentColor" />
                  {craftsman.rating.toFixed(1)}
                  {craftsman.reviews ? <span>({craftsman.reviews} reviews)</span> : null}
                </>
              ) : (
                <span>New on KaaKazini</span>
              )}
            </div>
          </div>
          {craftsman.vehicle && <span className="kz-plate">{craftsman.vehicle}</span>}
        </div>

        <div className="kz-actions">
          <button type="button" className="kz-btn white" onClick={onMessage} disabled={!onMessage}>
            <Icon name="message" size={17} /> Message
          </button>
          {craftsman.phone ? (
            <a className="kz-btn white" href={`tel:${craftsman.phone}`}>
              <Icon name="phone" size={17} /> Call
            </a>
          ) : (
            <button type="button" className="kz-btn white" disabled>
              <Icon name="phone" size={17} /> Call
            </button>
          )}
        </div>
      </div>

      {(craftsman.phone || where || craftsman.skills.length > 0) && (
        <div className="kz-kv-list" style={{ borderTop: 0, marginTop: 8 }}>
          {craftsman.phone && <div className="kz-kv"><span>Phone</span><span>{craftsman.phone}</span></div>}
          {where && <div className="kz-kv"><span>Currently at</span><span>{where}</span></div>}
          {craftsman.skills.length > 0 && (
            <div className="kz-kv">
              <span>Skills</span>
              <span className="kz-chips">{craftsman.skills.slice(0, 5).map((s, i) => <span className="kz-chip" key={`${s}-${i}`}>{s}</span>)}</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function JobDetails({ job, mediaUrl, demo }) {
  const [open, setOpen] = useState(false);
  const photo = mediaUrl(job.media_url || job.media || job.image_url || job.photo || null);
  const where = job.address || job.location || 'No address';
  const service = job.service || job.service_name || job.title || 'Service request';

  return (
    <section className="kz-block" aria-label="Job details">
      <div className="kz-block-head"><h3 className="kz-block-title">Job details</h3></div>
      <div className="kz-det">
        {photo && !demo ? (
          <img className="kz-det-img" src={photo} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : (
          <div className="kz-det-ph"><Icon name="image" size={26} stroke={1.5} /></div>
        )}
        <div className="kz-det-body">
          <b>{service}</b>
          <p>{job.description || 'No description was added to this request.'}</p>
          <div className="kz-det-loc"><Icon name="pin" size={14} /> {where}</div>
        </div>
      </div>

      {open && (
        <div className="kz-kv-list">
          {job.budget ? <div className="kz-kv"><span>Your budget</span><span>{kes(job.budget)}</span></div> : null}
          {amountOf(job).source === 'quote' ? <div className="kz-kv"><span>Quoted</span><span>{kes(amountOf(job).total)}</span></div> : null}
          {job.schedule ? <div className="kz-kv"><span>Scheduled</span><span>{fmtDate(job.schedule)}, {fmtTime(job.schedule)}</span></div> : null}
          {job.location ? <div className="kz-kv"><span>County</span><span style={{ textTransform: 'capitalize' }}>{job.location}</span></div> : null}
          {(job.isUrgent || job.is_urgent) ? <div className="kz-kv"><span>Priority</span><span>Urgent</span></div> : null}
          <div className="kz-kv"><span>Reference</span><span>{`KZ-${job.id}`}</span></div>
        </div>
      )}

      <p className="kz-hint">
        <button type="button" className="kz-link" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? 'Show less' : 'Show more'}
        </button>
      </p>
    </section>
  );
}

function WorkPhotos({ proofs, stage, mediaUrl, onViewProof }) {
  const urls = proofs.map((p) => mediaUrl(p?.image_url || p?.image || (typeof p === 'string' ? p : null))).filter(Boolean);
  const extra = urls.length > 6 ? urls.length - 5 : 0;
  const shown = extra ? urls.slice(0, 5) : urls.slice(0, 6);
  const title = ['completed', 'paid', 'closed'].includes(stage) ? 'Completed work' : 'Work photos';

  return (
    <section className="kz-block" aria-label="Work photos">
      <div className="kz-block-head"><h3 className="kz-block-title">{title}</h3></div>
      {urls.length === 0 ? (
        <div className="kz-photos-empty">
          Your craftsman will add photos here when the work is done. Check the work in person before you pay.
        </div>
      ) : (
        <div className="kz-photos">
          {shown.map((url, i) => (
            <button type="button" className="kz-photo" key={`${url}-${i}`} onClick={() => onViewProof && onViewProof(proofs, i)} aria-label={`View work photo ${i + 1}`}>
              <img src={url} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            </button>
          ))}
          {extra > 0 && (
            <button type="button" className="kz-photo more" onClick={() => onViewProof && onViewProof(proofs, 5)} aria-label={`View ${extra} more photos`}>
              +{extra}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Receipt({ job, craftsmanName }) {
  const { total, source, fee } = amountOf(job);
  return (
    <div className="kz-rcpt">
      <h4>Receipt #{String(job?.id ?? '').toUpperCase()}</h4>
      <div className="kz-rcpt-row"><span>Service</span><span>{job?.service || job?.service_name || 'Service request'}</span></div>
      <div className="kz-rcpt-row"><span>Craftsman</span><span>{craftsmanName || 'Not assigned'}</span></div>
      {fee ? <div className="kz-rcpt-row"><span>Service cost</span><span>{kes(total - fee)}</span></div> : null}
      {fee ? <div className="kz-rcpt-row"><span>Platform fee</span><span>{kes(fee)}</span></div> : null}
      <div className="kz-rcpt-row total"><span>{source === 'budget' ? 'Your budget' : 'Total'}</span><span>{total ? kes(total) : 'Not set'}</span></div>
      <p>
        {source === 'budget'
          ? 'This shows your budget. Your M-Pesa confirmation message has the amount actually charged.'
          : 'Keep this receipt for your records.'}
      </p>
      <button type="button" className="kz-btn light sm" onClick={() => window.print()}>
        <Icon name="printer" size={14} /> Print
      </button>
    </div>
  );
}

function PaymentCard({ job, stage, craftsmanName, proofsCount, demo, onPay }) {
  const [showReceipt, setShowReceipt] = useState(false);
  const { total, source, fee } = amountOf(job);
  const isEstimate = source === 'budget';
  const settled = stage === 'paid' || stage === 'closed';
  const ready = stage === 'completed' && total > 0;

  let hint = null;
  if (!settled && !ready) {
    if (!total) hint = 'The final amount is confirmed once the craftsman\'s quote is approved.';
    else hint = 'Payment opens once the work is marked complete.';
  } else if (!settled && ready) {
    if (proofsCount === 0 && !demo) hint = 'No work photos were added. Check the work in person before you pay.';
    else hint = isEstimate
      ? 'This is your budget. The final amount is confirmed when you pay.'
      : 'Nothing is charged until you tap Pay.';
  }

  return (
    <section className="kz-block kz-paycard" aria-label="Payment">
      <div className="kz-block-head"><h3 className="kz-block-title">Payment</h3></div>
      {fee ? (
        <>
          <div className="kz-pay-row"><span>Service cost</span><span>{kes(total - fee)}</span></div>
          <div className="kz-pay-row"><span>Platform fee</span><span>{kes(fee)}</span></div>
        </>
      ) : null}
      <div className="kz-pay-row total">
        <span>{isEstimate ? 'Your budget' : 'Total'}</span>
        <span>{total ? kes(total) : 'Not set'}</span>
      </div>

      <div className="kz-pay-actions" style={{ justifyItems: 'stretch' }}>
        {settled ? (
          <div className="kz-paid">
            <span className="kz-round"><Icon name="check" size={14} stroke={3} /></span> Paid
          </div>
        ) : (
          <button type="button" className="kz-btn pay block" onClick={onPay} disabled={!ready}>
            <span className="kz-mpesa">M-PESA</span> {isEstimate || !total ? 'Pay with M-Pesa' : `Pay ${kes(total)}`}
          </button>
        )}
        {settled && (
          <button type="button" className="kz-link" onClick={() => setShowReceipt((v) => !v)}>
            {showReceipt ? 'Hide receipt' : 'View receipt'}
          </button>
        )}
      </div>

      {hint && <p className="kz-hint">{hint}</p>}
      {settled && showReceipt && <Receipt job={job} craftsmanName={craftsmanName} />}
    </section>
  );
}

function RateForm({ onSubmit }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!rating) {
      setError('Choose a star rating first.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSubmit(rating, comment.trim());
    } catch (err) {
      setError(err?.message || 'Could not submit your rating. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onStarKey = (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      setRating((r) => Math.min(5, (r || 0) + 1));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      setRating((r) => Math.max(1, (r || 2) - 1));
    }
  };

  return (
    <section className="kz-block" aria-label="Rate your craftsman">
      <div className="kz-block-head"><h3 className="kz-block-title">How was the work?</h3></div>
      <p className="kz-hint" style={{ marginTop: -6 }}>Your rating helps other clients choose.</p>
      <div className="kz-stars" role="radiogroup" aria-label="Rate your craftsman" onKeyDown={onStarKey}>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={rating === value}
            aria-label={`${value} star${value > 1 ? 's' : ''}`}
            tabIndex={rating === value || (!rating && value === 1) ? 0 : -1}
            className={rating >= value ? 'on' : ''}
            onClick={() => { setRating(value); setError(''); }}
          >
            ★
          </button>
        ))}
      </div>
      <label className="kz-sr" htmlFor="kz-comment">Comment</label>
      <textarea
        id="kz-comment"
        className="kz-textarea"
        placeholder="Add a comment (optional)"
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />
      {error && <p role="alert" className="kz-err">{error}</p>}
      <div style={{ marginTop: 14 }}>
        <button className="kz-btn block" type="button" onClick={submit} disabled={busy}>
          {busy ? 'Submitting' : 'Submit rating'}
        </button>
      </div>
    </section>
  );
}


/* ───────────── main component ───────────── */

export default function DispatchTrackerTab({
  jobs = [],
  selectedId,
  onSelect,
  onRefresh,
  onPay,
  onRate,
  onCancel,
  onRetry,
  onRequest,
  onViewProof,
  onQuoteDecide,
  onViewQuote,
  onBack,
  onMessage,
  mediaUrl = identity,
}) {
  const [ratedIds, setRatedIds] = useState(new Set());
  const [demo, setDemo] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showUpdates, setShowUpdates] = useState(true);

  // Parents often pass a fresh mediaUrl and onRefresh every render. Wrap the
  // first so empty values never reach it, and keep the second in a ref so the
  // polling interval isn't torn down on every render.
  const media = (value) => (value ? mediaUrl(value) : null);
  const refreshRef = useRef(onRefresh);
  useEffect(() => { refreshRef.current = onRefresh; });
  const canRefresh = !!onRefresh;

  const live = useMemo(
    () => jobs.filter((job) => !isCancelled(job) && stageOf(job, ratedIds.has(job.id)) !== 'closed'),
    [jobs, ratedIds]
  );

  const job = demo || jobs.find((item) => String(item.id) === String(selectedId)) || live[0] || null;
  const stage = job ? stageOf(job, ratedIds.has(job.id)) : null;
  const cancelled = job ? isCancelled(job) : false;
  const noMatch = !!job && !cancelled && isNoMatch(job);
  const currentIndex = stage === 'closed' ? STAGES.length : idx(stage);
  const craftsman = job ? cInfo(job) : {};
  const searching = !noMatch && (stage === 'requested' || stage === 'matching');
  const proofs = Array.isArray(job?.proof_images) ? job.proof_images : [];

  const { geo, status: geoStatus } = useGeocode(job?.address, job?.location);

  const jobId = job?.id;
  useEffect(() => {
    if (demo || !jobId || !canRefresh || ['closed', 'paid'].includes(stage) || cancelled) return undefined;
    const timer = setInterval(() => { if (refreshRef.current) refreshRef.current(); }, 6000);
    return () => clearInterval(timer);
  }, [demo, jobId, canRefresh, stage, cancelled]);

  useEffect(() => {
    if (!demo || ['completed', 'paid', 'closed'].includes(demo.dispatch_status)) return undefined;
    const order = ['requested', 'matching', 'assigned', 'on_way', 'arrived', 'in_progress', 'completed'];
    const timer = setTimeout(() => {
      setDemo((current) => {
        if (!current) return current;
        const next = order[order.indexOf(current.dispatch_status) + 1];
        if (!next) return current;
        const nextCraftsman = next === 'assigned'
          ? {
              id: 1, full_name: 'James Mwangi', average_rating: 4.8, review_count: 124, phone: '+254 712 345 678',
              primary_service: 'Plumber', years_experience: 5, vehicle_plate: 'KDA 784Z',
              skills: ['Pipe repair', 'Sink repair', 'General plumbing'],
            }
          : current.craftsman;
        return {
          ...current,
          dispatch_status: next,
          craftsman: nextCraftsman,
          eta_minutes: next === 'on_way' ? 12 : null,
          dispatch_attempts: next === 'matching' ? 2 : current.dispatch_attempts,
          stage_times: { ...current.stage_times, [next]: new Date().toISOString() },
        };
      });
    }, 3200);
    return () => clearTimeout(timer);
  }, [demo]);

  const startDemo = () => setDemo({
    id: 'DEMO',
    service: 'Plumbing, sink repair',
    description: 'The kitchen sink is leaking from the bottom. Please fix the leak and check everything is working.',
    address: 'Westlands, Nairobi',
    budget: 2750,
    dispatch_status: 'requested',
    dispatch_attempts: 1,
    schedule: new Date().toISOString(),
    created_at: new Date().toISOString(),
    stage_times: { requested: new Date().toISOString() },
    proof_images: [],
  });

  const doRate = async (rating, comment) => {
    if (demo) {
      setDemo((current) => ({ ...current, dispatch_status: 'closed', rating }));
      return;
    }
    if (!onRate) throw new Error('Ratings are not available right now. Please try again later.');
    await onRate(job, rating, comment);
    setRatedIds((previous) => new Set([...previous, job.id]));
  };

  const doCancel = async () => {
    if (!window.confirm('Cancel this service request?')) return;
    if (demo) {
      setDemo(null);
      return;
    }
    if (!onCancel) return;
    setCancelling(true);
    try {
      await onCancel(job.id);
    } catch (error) {
      window.alert(error?.response?.data?.detail || error?.message || 'Could not cancel the request. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const doRetry = async () => {
    if (!onRetry) return;
    setRetrying(true);
    try {
      await onRetry(job.id);
    } finally {
      setRetrying(false);
    }
  };

  const doPay = () => {
    if (demo) {
      setDemo((current) => ({ ...current, dispatch_status: 'paid' }));
      return;
    }
    if (onPay) onPay(job);
  };

  /* empty state */
  if (!job) {
    return (
      <div className="kz">
        <style>{CSS}</style>
        <section className="kz-empty">
          <div className="kz-empty-ico" aria-hidden="true"><Icon name="radar" size={32} stroke={1.8} /></div>
          <h3>No active requests</h3>
          <p>When you request a service, you can follow it here from matching to payment.</p>
          <div className="kz-empty-btns">
            {onRequest && <button className="kz-btn" type="button" onClick={onRequest}>Request a service</button>}
            {process.env.NODE_ENV !== 'production' && (
              <button className="kz-btn light" type="button" onClick={startDemo}>Preview tracking</button>
            )}
          </div>
        </section>
      </div>
    );
  }

  const created = rawStageTime(job, 'requested') || job.schedule;
  const dateLabel = fmtDate(job.schedule || created);
  const code = job.reference || job.job_code || (demo ? 'KZ-7845' : `KZ-${job.id}`);
  const canCancel = !demo && (searching || noMatch || stage === 'assigned') && !cancelled && onCancel;
  const quotePending = job.status === 'Quote Submitted' && !demo;
  const hasPlace = !!(job.address || job.location);
  const [headline, lead] = cancelled
    ? ['Request cancelled', 'You can start a new request any time.']
    : noMatch
      ? ['No craftsman available', 'We could not find someone for this job right now.']
      : NOTE[stage];
  const eta = stage === 'on_way'
    ? { big: Number(job.eta_minutes) > 0 ? `${job.eta_minutes} min` : 'Soon', label: 'Arriving' }
    : ['arrived', 'in_progress'].includes(stage)
      ? { big: 'Here', label: 'At your address', done: true }
      : null;

  const controls = (
      <div className={`kz-top${cancelled ? '' : ' float'}`}>
        <div className="kz-top-l">
          {onBack && (
            <button type="button" className="kz-back" onClick={onBack} aria-label="Back to my requests">
              <Icon name="arrowLeft" size={20} stroke={2.2} />
            </button>
          )}
          <span className="kz-ref">Job {code}</span>
        </div>
        <div className="kz-top-r">
          {onRefresh && !demo && (
            <button type="button" className="kz-btn light sm" onClick={onRefresh}>
              <Icon name="refresh" size={14} stroke={2} /> Refresh
            </button>
          )}
          {canCancel && (
            <button type="button" className="kz-btn light sm kz-cancel" onClick={doCancel} disabled={cancelling}>
              {cancelling ? 'Cancelling' : 'Cancel request'}
            </button>
          )}
        </div>
      </div>
  );

  return (
    <div className="kz">
      <style>{CSS}</style>

      {cancelled && controls}

      {demo && (
        <div className="kz-note" role="status" style={{ marginTop: 0, marginBottom: 16 }}>
          <Icon name="alert" size={16} />
          <span>
            Preview with sample data. It moves forward on its own.
            <button type="button" className="kz-link" onClick={() => setDemo(null)}>Exit preview</button>
          </span>
        </div>
      )}

      {!demo && live.length > 1 && (
        <div className="kz-picker" aria-label="Choose a service request">
          {live.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`kz-btn sm${item.id === job.id ? '' : ' light'}`}
              onClick={() => onSelect && onSelect(item.id)}
            >
              {item.service || item.service_name || 'Service'} #{item.id}
            </button>
          ))}
        </div>
      )}

      {!cancelled && (
        <div className="kz-hero">
          {controls}
          <JobMap geo={geo} status={geoStatus} hasPlace={hasPlace} place={job.address || job.location} />
        </div>
      )}

      <div className={`kz-sheet${!cancelled ? ' over' : ''}`}>
        <span className="kz-grab" aria-hidden="true" />

        <div className="kz-cols">
          <div className="kz-col">
            <div className={`kz-status-card${cancelled ? ' off' : ''}`}>
              <div className="kz-head">
                <span className="kz-status" aria-hidden="true">
                  <Icon name={cancelled || noMatch ? 'x' : STAGES[idx(stage)].ico} size={22} stroke={2} />
                </span>
                <div className="kz-head-text">
                  <h2 className="kz-h">{headline}</h2>
                  <p className="kz-lead">{lead}</p>
                </div>
                {eta && !cancelled && (
                  <div className={`kz-eta${eta.done ? ' done' : ''}`}>
                    <small>{eta.label}</small>
                    {eta.big}
                  </div>
                )}
              </div>

              {!cancelled && !noMatch && <Progress currentIndex={currentIndex} searching={searching} stage={stage} />}
            </div>

            {noMatch && !demo && (
              <div className="kz-quote" role="status">
                <b>Nobody suitable was free nearby</b>
                <p>Try again and we will look for a craftsman once more, or cancel and request later.</p>
                <div className="kz-quote-btns">
                  {onRetry && (
                    <button className="kz-btn sm" type="button" onClick={doRetry} disabled={retrying}>
                      {retrying ? 'Searching' : 'Try again'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {quotePending && (
              <div className="kz-quote" role="status">
                <b>A quote is waiting for your decision</b>
                <p>Review the line items, then approve or reject it.</p>
                <div className="kz-quote-btns">
                  <button className="kz-btn sm light" type="button" onClick={() => onViewQuote && onViewQuote(job)}>View quote</button>
                  <button className="kz-btn sm" type="button" onClick={() => onQuoteDecide && onQuoteDecide(job.id, 'approve')}>Approve</button>
                  <button className="kz-btn sm light kz-cancel" type="button" onClick={() => onQuoteDecide && onQuoteDecide(job.id, 'reject')}>Reject</button>
                </div>
              </div>
            )}

            {stage === 'matching' && !noMatch && Number(job.dispatch_attempts) > 1 && (
              <div className="kz-note" role="status">
                <Icon name="refresh" size={16} />
                <span>The last craftsman couldn't come, so we're trying the next suitable match (attempt {job.dispatch_attempts}). You don't need to send another request.</span>
              </div>
            )}

            <div style={{ marginTop: 28 }}>
              {!cancelled && searching && <CraftsmanSkeleton />}

              {!cancelled && !searching && !noMatch && craftsman.name && (
                <Craftsman craftsman={craftsman} job={job} stage={stage} mediaUrl={media} onMessage={onMessage} />
              )}

              {!cancelled && !noMatch && (
                <section className="kz-block" aria-label="Updates">
                  <div className="kz-block-head">
                    <h3 className="kz-block-title">Updates</h3>
                    <button type="button" className="kz-link" onClick={() => setShowUpdates((v) => !v)} aria-expanded={showUpdates}>
                      {showUpdates ? 'Hide' : 'Show all'}
                    </button>
                  </div>
                  {showUpdates ? (
                    <Updates job={job} currentIndex={currentIndex} />
                  ) : (
                    <p className="kz-hint" style={{ marginTop: 0 }}>
                      {STATUS_LABEL[stage]}{stageTime(job, stage) ? ` at ${stageTime(job, stage)}` : ''}
                      {dateLabel ? `, requested for ${dateLabel}` : ''}
                    </p>
                  )}
                </section>
              )}
            </div>
          </div>

          <div className="kz-col kz-side">
            <JobDetails job={job} mediaUrl={media} demo={!!demo} />
            {!cancelled && !noMatch && <WorkPhotos proofs={proofs} stage={stage} mediaUrl={media} onViewProof={onViewProof} />}
            {!cancelled && !noMatch && (
              <PaymentCard
                job={job}
                stage={stage}
                craftsmanName={craftsman.name}
                proofsCount={proofs.length}
                demo={!!demo}
                onPay={doPay}
              />
            )}
          </div>
        </div>

        {stage === 'paid' && !cancelled && (
          <div style={{ maxWidth: 520, marginTop: 36 }}><RateForm onSubmit={doRate} /></div>
        )}

        {stage === 'closed' && !cancelled && (
          <div style={{ marginTop: 28 }}>
            <button className="kz-btn" type="button" onClick={demo ? () => setDemo(null) : onRequest}>
              Request another service
            </button>
          </div>
        )}
      </div>
    </div>
  );
}